import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  InputGroup,
  InputRightAddon,
  TextInput,
} from '@ouster/webviz-react-components';

import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { AdminDashboardOrgBrief } from './types';
import { useStoreActions } from '../../reducers';
import { formatBytesToGB } from '../../utils/misc';

type AdminStorageConfigProps = {
  handleClose: () => void;
} & Pick<
  AdminDashboardOrgBrief,
  'organization_id' | 'allowed_space' | 'upload_limit'
>;

export default function AdminStorageConfig({
  organization_id,
  allowed_space,
  // upload_limit,
  handleClose,
}: AdminStorageConfigProps) {
  const [allowedSpace, setAllowedSpace] = useState(allowed_space);
  // const [uploadLimit, setUploadLimit] = useState(upload_limit);
  const [isUpdating, setIsUpdating] = useState(false);
  const queryClient = useQueryClient();

  const { updateOrgAllowedSpace } = useStoreActions(
    (actions) => actions.adminDashboard
  );

  const handleConfigChange = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (allowedSpace <= 0) return;

    try {
      setIsUpdating(true);
      await updateOrgAllowedSpace({
        allowed_space: allowedSpace,
        organization_id,
      });
      queryClient.invalidateQueries({
        queryKey: ['adminDashboardSummary'],
        refetchType: 'all',
      });

      handleClose();
    } catch (err) {
      console.error(err);
    } finally {
      setIsUpdating(false);
    }
  };
  return (
    <Box
      as="form"
      display="grid"
      rowGap={6}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onSubmit={handleConfigChange}
      minW={480}
      data-testid="admin-storage-config-form"
    >
      <FormControl paddingInline={0}>
        <FormLabel>Storage Limit</FormLabel>
        <InputGroup>
          <TextInput
            type="number"
            isRequired
            value={allowedSpace / 1_000_000_000} // Convert bytes to GB for display
            onChange={(e) =>
              setAllowedSpace(parseFloat(e.target.value) * 1_000_000_000)
            }
            autoFocus
            onFocus={(e) => e.target.select()}
            data-testid="admin-storage-config-input"
          />
          <InputRightAddon>GB</InputRightAddon>
        </InputGroup>
        <FormHelperText minH={8}>
          {allowedSpace !== allowed_space ? (
            <>
              The storage limit will be changed from{' '}
              <Box as="span" fontWeight={600}>
                {formatBytesToGB(allowed_space)}
              </Box>{' '}
              to{' '}
              <Box as="span" fontWeight={600}>
                {formatBytesToGB(allowedSpace)}
              </Box>
            </>
          ) : (
            ''
          )}
        </FormHelperText>
      </FormControl>
      {/* 
        Comment this out till we support that feature in upload
      */}
      {/* <FormControl paddingInline={0}>
        <FormLabel>Upload Limit</FormLabel>
        <InputGroup>
          <TextInput
            type="number"
            isRequired
            value={uploadLimit / 1_000_000_000}
            onChange={(e) =>
              setUploadLimit(parseFloat(e.target.value) * 1_000_000_000)
            }
          />
          <InputRightAddon>GB</InputRightAddon>
        </InputGroup>
      </FormControl> */}
      <Button
        minWidth={150}
        justifySelf="end"
        type="submit"
        isDisabled={isUpdating}
        data-testid="admin-storage-config-save"
      >
        Save
      </Button>
    </Box>
  );
}
